import React from 'react';
import { Box, Text, HStack, VStack, Icon, Badge, Spinner } from '@chakra-ui/react';
import { FiCheckCircle, FiSmartphone } from 'react-icons/fi';
import Card from 'components/card/Card';
import { CopyField, ago } from './_shared';

/**
 * The last thing that can go wrong, and the only check that proves the rest.
 *
 * ★ IT VERIFIES THE WHOLE CHAIN, NOT THE WEBHOOK.
 * Meta → webhook → tenant routing → this number → the CRM → a conversation.
 * Every step above this one can be green while nothing arrives, and when that
 * happens the cause is almost always a webhook that verified but was never
 * subscribed to the `messages` field. A handshake tick cannot tell you that; a
 * message that did or did not appear can.
 *
 * ★ PER NUMBER, WHICH IS WHY THIS CARD EXISTS AT ALL.
 * The tenant's webhook row records that SOME message reached this workspace, so
 * with a second number it is green before you begin. The connection layer now
 * stamps the number a customer actually wrote to, and this asks about the one
 * being set up.
 *
 * ★ NOTHING TO CLICK, DELIBERATELY. There is no button that can make a customer
 * message arrive, and a "check now" that only refetched would imply the delay
 * was ours. The screen polls while this step is current and stops when it is
 * answered — see `pollingInterval` on the setup read.
 */
export default function InboundTest({ channel, isPolling }) {
	const received = channel?.lastInboundAt ?? null;

	return (
		<Card
			bg="bg.surface"
			borderWidth="1px"
			borderColor={received ? 'border.default' : 'warning'}
		>
			<HStack spacing="2" mb="1">
				<Badge colorScheme={received ? 'green' : 'gray'} variant="subtle" fontSize="0.55rem">
					{received ? 'Done' : 'Final check'}
				</Badge>
				<Text fontSize="sm" fontWeight="bold" color="text.primary">
					{received ? 'Messages are arriving' : 'Send yourself a message'}
				</Text>
			</HStack>

			{received ? (
				<HStack spacing="2" mt="2">
					<Icon as={FiCheckCircle} color="success" />
					<Text fontSize="xs" color="text.secondary">
						A customer message reached this number {ago(received)}. Meta, the webhook, the routing
						and the inbox are all working.
					</Text>
				</HStack>
			) : (
				<VStack align="stretch" spacing="3" mt="2">
					<Text fontSize="xs" color="text.muted" lineHeight="1.7">
						From any phone, send a WhatsApp message to the number below. Nothing above proves a
						message can actually reach you — this does.
					</Text>

					<CopyField
						label="Send a WhatsApp to"
						value={channel?.displayPhoneNumber}
						hint="This number has no display number yet."
					/>

					<HStack spacing="2">
						{isPolling ? <Spinner size="xs" color="text.accent" /> : <Icon as={FiSmartphone} color="text.muted" />}
						<Text fontSize="xs" color={isPolling ? 'text.accent' : 'text.muted'}>
							{isPolling ? 'Watching for it…' : 'Waiting for the first message.'}
						</Text>
					</HStack>

					{/* ★ THE TWO WAYS THIS FAILS SILENTLY, both of which look identical
					    from here: the message simply never appears. Neither can be
					    detected before a real message is sent, which is why they are
					    named rather than checked. */}
					<Box bg="bg.subtle" borderRadius="md" p="3">
						<Text fontSize="0.7rem" color="text.secondary" lineHeight="1.7" mb="2">
							<b>Nothing arriving?</b> Two causes, both invisible until now:
						</Text>
						<Text fontSize="0.7rem" color="text.secondary" lineHeight="1.7" mb="1.5">
							1. In your Meta app, under <b>WhatsApp → Configuration → Webhook fields</b>, check
							that <b>messages</b> is subscribed. Meta verifies a callback URL happily and then
							delivers nothing without it.
						</Text>
						<Text fontSize="0.7rem" color="text.secondary" lineHeight="1.7">
							2. Check your <b>app secret</b> in Admin Settings → Configuration, under the service{' '}
							<b>whatsapp</b>. Meta signs every message with it; if it is missing or wrong, each
							one is refused before it reaches the inbox. Sending is unaffected, which is why
							this looks like a webhook fault.
						</Text>
					</Box>
				</VStack>
			)}
		</Card>
	);
}
